import { type PropsWithChildren, type ReactElement } from 'react';
import NextLink from 'next/link';
import Router from 'next/router';
import {
  Link,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

function useErrorMessage({ error, resetError }: { error: Error, resetError: () => void }): ReactElement {
  const { closeSnackbar } = useSnackbar();

  Router.events.on('routeChangeComplete', closeSnackbar);

  switch (error.message) {
    case 'api-auth-validation.userExists':
      return (
        <div>
          <Typography variant="h6">
            Oops! Looks like that email is already registered for a different account.
          </Typography>
          <Typography variant="body1" gutterBottom>
            If that&apos;s your correct email, then it seems you already have an account associated with it.
            Please&nbsp;
            <Link component={NextLink} href="/auth/login" onClick={() => closeSnackbar()}>log in</Link>
            &nbsp;with your credentials for that account to access it, or enter a different email.
          </Typography>
          <Typography variant="body1" gutterBottom>
            If you need any further help changing your email or accessing your account,
            hit us up at&nbsp;
            <Link component={NextLink} href="mailto:support@anychart.com">support@anychart.com</Link>
            &nbsp;and we&apos;ll gladly help you sort it out.
          </Typography>
        </div>
      );
    case 'api-auth-validation.badCredentials':
      return (
        <div>
          <Typography variant="h6">Uh oh! Looks like your password or email is incorrect.</Typography>
          <Typography variant="body1" gutterBottom>
            Please double-check and try again, or&nbsp;
            <Link component={NextLink} href="/auth/reset-password" onClick={() => closeSnackbar()}>
              reset your password
            </Link>
            &nbsp;or&nbsp;
            <Link component={NextLink} href="/auth/signup" onClick={() => closeSnackbar()}>create an account</Link>
            .
          </Typography>
          <Typography variant="body1" gutterBottom>
            For more help, contact&nbsp;
            <Link component={NextLink} href="mailto:support@anychart.com">support@anychart.com</Link>
            .
          </Typography>
        </div>
      );
    case 'api-auth-validation.notConfirmed':
      return (
        <div>
          <Typography variant="h6">
            Oopsie! Seems like you haven&apos;t confirmed your account email yet.
          </Typography>
          <Typography variant="body1" gutterBottom>
            Please check your inbox for our verification email or click&nbsp;
            <Link
              sx={{ cursor: 'pointer' }}
              href="/auth/email-confirmation/resend"
              onClick={() => {
                resetError();
                closeSnackbar();
              }}
            >
              here
            </Link>
            &nbsp;to resend it. For more help, contact&nbsp;
            <Link component={NextLink} href="mailto:support@anychart.com">support@anychart.com</Link>
            .
          </Typography>
        </div>
      );
    default:
      return (
        <Typography variant="caption">{error.toString()}</Typography>
      );
  }
}

type FallbackProps = PropsWithChildren<{
  error: Error;
  resetError(): void;
}>;

export default function FallbackComponent({ children, ...fallbackProps }: FallbackProps): ReactElement {
  const { enqueueSnackbar } = useSnackbar();
  const errorMessage = useErrorMessage(fallbackProps);
  enqueueSnackbar(errorMessage, {
    variant: 'error',
    preventDuplicate: true,
    onClose: fallbackProps.resetError,
    persist: true,
  });

  return children! as ReactElement;
}
